import type { ReactNode } from "react";
import katex from "katex";
import "katex/dist/katex.min.css";
import ScientificText from "../ScientificText";

// Figure/table legends are pre-simplified API-side (see apps/api
// `lib/tex-to-text.ts`): simple formulas are already flattened to UTF-8, so
// what reaches here is prose plus the occasional COMPLEX formula (fractions,
// integrals, matrices) still in its `$…$`, `$$…$$` or `\(…\)` delimiters. A
// legend stored before the API read `\(…\)` still carries simple formulas in
// them. Render every formula with KaTeX; everything else goes through
// ScientificText for `^{}`/`_{}` sub/sups.
const FORMULA_RE = /\$\$([^$]+?)\$\$|\$([^$]+?)\$|\\\((.+?)\\\)/g;

type LegendPart =
  | { kind: "text"; value: string }
  // `source` is the formula as written, delimiters included.
  | { kind: "math"; tex: string; display: boolean; source: string };

function splitLegendParts(legend: string): LegendPart[] {
  const parts: LegendPart[] = [];
  let cursor = 0;
  for (const match of legend.matchAll(FORMULA_RE)) {
    const start = match.index;
    if (start > cursor) {
      parts.push({ kind: "text", value: legend.slice(cursor, start) });
    }
    const [source, dollarDisplay, dollarInline, parenInline] = match;
    const display = dollarDisplay !== undefined;
    const tex = [dollarDisplay, dollarInline, parenInline].find(
      (group) => group !== undefined
    );
    // One of the three groups fired for a match, but narrow explicitly so the
    // compiler carries it without an assertion.
    if (tex === undefined) {
      throw new Error("Invariant: formula match with no captured group");
    }
    parts.push({ kind: "math", tex, display, source });
    cursor = start + source.length;
  }
  if (cursor < legend.length) {
    parts.push({ kind: "text", value: legend.slice(cursor) });
  }
  return parts;
}

// KaTeX output is generated from our own parse of the legend and is safe to
// inject. `throwOnError: true` lets us catch malformed TeX and fall back to
// the literal source text (delimiters included) instead of KaTeX's red error
// box — a garbled legend should still read as its raw characters.
function renderMathHtml(tex: string, display: boolean): string | null {
  try {
    return katex.renderToString(tex, {
      throwOnError: true,
      displayMode: display,
    });
  } catch {
    return null;
  }
}

export default function LegendText({
  children,
}: {
  children: string;
}): ReactNode {
  return (
    <>
      {splitLegendParts(children).map((part, index) => {
        if (part.kind === "text") {
          return <ScientificText key={index}>{part.value}</ScientificText>;
        }
        const html = renderMathHtml(part.tex, part.display);
        if (html === null) {
          return <span key={index}>{part.source}</span>;
        }
        return <span key={index} dangerouslySetInnerHTML={{ __html: html }} />;
      })}
    </>
  );
}
